import React, { useState } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import { Drawer } from '../../../../components/drawers';
import toast from '../../../../utils/toast';
import RiskyIdFormFields from './RiskyIdFormFields';

interface AddRiskyIdFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  id_number: string;
  reason: string;
  active: boolean;
}

const AddRiskyIdForm: React.FC<AddRiskyIdFormProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<FormData>({
    id_number: '',
    reason: '',
    active: true
  });
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setFormData({ id_number: '', reason: '', active: true });
    onClose();
  };

  const handleIdNumberChange = (value: string) => {
    setFormData(prev => ({ ...prev, id_number: value }));
  };

  const handleReasonChange = (value: string) => {
    setFormData(prev => ({ ...prev, reason: value }));
  };

  const handleActiveChange = (value: boolean) => {
    setFormData(prev => ({ ...prev, active: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${environment.baseUrl}/risky-id-numbers/`, formData);
      toast.success('Risky ID number added successfully');
      onSuccess();
      handleClose();
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.id_number) {
        toast.error('This ID number has already been flagged');
      } else {
        toast.error(err.response?.data?.message || 'Failed to add risky ID number');
      }
    } finally {
      setLoading(false);
    }
  };

  const isValid = formData.id_number.trim().length > 0;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <Drawer.Header title="Add Risky ID Number" onClose={handleClose} />

      <Drawer.Content>
        <form onSubmit={handleSubmit}>
          <RiskyIdFormFields
            idNumber={formData.id_number}
            reason={formData.reason}
            active={formData.active}
            onIdNumberChange={handleIdNumberChange}
            onReasonChange={handleReasonChange}
            onActiveChange={handleActiveChange}
            showAlert
          />
        </form>
      </Drawer.Content>

      <Drawer.Footer fullWidth>
        <Drawer.PrimaryButton
          type="submit"
          onClick={handleSubmit}
          disabled={!isValid || loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Drawer.PrimaryButton>
      </Drawer.Footer>
    </Drawer>
  );
};

export default AddRiskyIdForm;